/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import { Loading } from "notiflix/build/notiflix-loading-aio";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import MDTypography from "components/MDTypography";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import ControlledAutoComplete from "components/FormInputs/ControlledAutoComplete";
import ControlledTextInput from "components/FormInputs/ControlledTextInput";
import { useLazyGetResellersByCompanyIdQuery } from "services/resellers";
import { useGetAllCompaniesQuery } from "services/companies";
import { useForm } from "react-hook-form";
import { sendEmailSchema } from "../schemas/_model";
import { yupResolver } from "@hookform/resolvers/yup";
import { globalCountry } from "store/entities/countries";
import { useSelector } from "react-redux";
import { Block } from "notiflix/build/notiflix-block-aio";
import ReactQuill from "react-quill";
import MDBox from "components/MDBox";
import Box from "@mui/material/Box";
import { CardActions } from "@mui/material";
import MDButton from "components/MDButton";
import { Notify } from "notiflix";
import { replaceSingleQuoteToSQLFriendly } from "utils/helperFunctions";
import { useSendEmailMutation } from "services/send_email";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const initialFormState = {
  to: "",
  cc: "",
  bcc: "",
  subject: "",
  body: "",
};

function SendEmail() {
  const selectedCountry = useSelector(globalCountry);
  const [companyResellers, setCompanyResellers] = useState([]);
  const navigate = useHistory();
  const [getResellersByCompanyId] = useLazyGetResellersByCompanyIdQuery();
  const { control, watch, setValue, handleSubmit } = useForm({
    resolver: yupResolver(sendEmailSchema),
    defaultValues: initialFormState,
  });
  const { data: companyList = [] } = useGetAllCompaniesQuery(selectedCountry.id);
  const companyInput = watch("companyId");
  const resellerInput = watch("resellerId");
  const [editorBodyOnChange, setEditorBodyOnChange] = useState("");
  const [postSendEmailData] = useSendEmailMutation();

  useEffect(() => {
    const fetchResellers = async () => {
      if (companyInput) {
        if (
          companyList.filter((item) => item.id == companyInput) &&
          companyList.filter((item) => item.id == companyInput).length > 0
        ) {
          Block.dots("#resellerElement", { svgColor: "rgba(159, 3, 3, 0.8)" });
          const resellersResponse = await getResellersByCompanyId(companyInput);
          if (resellersResponse.data && resellersResponse.data.length > 0) {
            setCompanyResellers(resellersResponse.data);
            Block.remove("#resellerElement", 2000);
          }
        }
        setValue("to", "");
      } else {
        setCompanyResellers([]);
      }
    };

    fetchResellers();
  }, [companyInput]);

  useEffect(() => {
    let tempReseller = companyResellers.find((x) => x.id == resellerInput);
    if (tempReseller) {
      setValue("to", tempReseller.email);
    }
  }, [resellerInput]);

  const onSubmit = async (data) => {
    if (editorBodyOnChange === undefined || editorBodyOnChange == "") {
      Notify.failure("You must add the body!");
      return;
    }
    Loading.dots({
      svgColor: "rgba(159, 3, 3, 0.8)",
    });
    data.body = replaceSingleQuoteToSQLFriendly(editorBodyOnChange);
    delete data.resellerId;
    delete data.companyyId;
    const res = await postSendEmailData(data);
    if (res.data?.status == "success") {
      Notify.success("Email sent successfully!");
    } else {
      Notify.failure("Something went wrong. Please try again!");
    }
    Loading.remove(500);
    navigate.push("/user/email-history");
  };
  return (
    <Card>
      <CardHeader title={<MDTypography color="info">Send Email</MDTypography>} />
      <CardContent>
        <GridContainer sx={{ px: 1, mt: 1, pb: 1 }}>
          <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
            <ControlledAutoComplete
              id="companyId"
              name="companyId"
              control={control}
              options={companyList}
              optionLabelProperty="name"
              label="Company"
            />
          </GridItem>
          <GridItem xs={12} sm={12} md={4} lg={4} xl={4} id="resellerElement">
            <ControlledAutoComplete
              id="resellerId"
              name="resellerId"
              control={control}
              options={companyResellers}
              optionLabelProperty="email"
              label="Reseller"
              disabled={companyResellers.length == 0}
            />
          </GridItem>
          <GridItem xs={12} sm={12} md={8} lg={8} xl={8} sx={{ mt: 2 }}>
            <ControlledTextInput
              name="to"
              control={control}
              labelText="To Address *"
              formControlProps={{
                fullWidth: true,
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={12} md={8} lg={8} xl={8} sx={{ mt: 2 }}>
            <ControlledTextInput
              name="subject"
              control={control}
              labelText="Subject *"
              formControlProps={{
                fullWidth: true,
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={12} md={4} lg={4} xl={4} sx={{ mt: 2 }}></GridItem>
          <GridItem xs={12} sm={12} md={4} lg={4} xl={4} sx={{ mt: 2 }}>
            <ControlledTextInput
              name="cc"
              control={control}
              labelText="CC Address"
              formControlProps={{
                fullWidth: true,
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={12} md={4} lg={4} xl={4} sx={{ mt: 2 }}>
            <ControlledTextInput
              name="bcc"
              control={control}
              labelText="BCC Address"
              formControlProps={{
                fullWidth: true,
              }}
            />
          </GridItem>
        </GridContainer>
        <br />
        <GridContainer>
          <GridItem xs={12} sm={12} md={8} lg={8} xl={8}>
            <MDBox
              sx={{
                border: "1px solid #d2d6da",
                borderRadius: "0.375rem",
                mb: 3,
                pt: 2,
                position: "relative",
                // "& .tox-tinymce": { border: "none", position: "relative" },
                "& .ql-editor": { minHeight: "250px" },
              }}
            >
              <MDTypography
                component="p"
                color="info"
                sx={{
                  lineHeight: "1.5",
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  transform: "translate(7px, -50%)",
                  backgroundColor: "#fff",
                  color: "#7b809a",
                  padding: "0 5px",
                  position: "absolute",
                  left: 0,
                  top: 0,
                  zIndex: 1,
                }}
              >
                Body
              </MDTypography>
              <ReactQuill
                theme="snow"
                value={editorBodyOnChange}
                onChange={setEditorBodyOnChange}
                modules={modules}
              />
            </MDBox>
          </GridItem>
        </GridContainer>
        <CardActions disableSpacing>
          * Required fields
          <Box sx={{ marginLeft: "auto" }}>
            <MDButton
              color="primary"
              variant="gradient"
              id="submit"
              onClick={handleSubmit(onSubmit, (e) => console.log(e))}
            >
              Send EMail
            </MDButton>
            <MDButton
              color="secondary"
              variant="gradient"
              onClick={() => navigate.goBack()}
              sx={{ ml: 1 }}
            >
              Cancel
            </MDButton>
          </Box>
        </CardActions>
      </CardContent>
    </Card>
  );
}

export default SendEmail;
